import { put, call, all, takeEvery, takeLatest } from 'redux-saga/effects'
import _ from 'lodash'
import { store as reduxStore } from 'index'
import {
  get as callLogs,
  getByID as getCallDetail,
  downloadRecording as downloadCallRecording,
  ChatApp,
  CallLog,
  CallLogsFilters,
  CallLogsListResponse,
  CallDetailResponse,
  DownloadRecordingError,
} from 'services/callLogs'
import {
  get as legacyCallLogs,
  getByID as legacyCallDetail,
  getParticipants as legacyCallParticipants,
  downloadRecording as downloadLegacyRecording,
  isLegacyFailure,
  LegacyApiErrorBody,
  LegacyCallsApp,
  LegacyCallParticipant,
  LegacyCallSessionsResponse,
  LegacyCallSessionsResult,
  LegacyCallParticipantsResponse,
  LegacyCallParticipantsResult,
  LegacyDownloadRecordingError,
} from 'services/callLogsLegacy'
import { getMultiple as getMultipleUsers } from 'services/users'
import { ChatUser, UsersListResponse } from 'services/users/types'
import { getAppCallsHost, getAppMetricHost, getAppHost } from 'helpers'
import actions from './actions'
import {
  LoosePagination,
  LegacyCallParticipantRow,
  LegacyParticipantPlatform,
} from './reducers'

/** Calls slice sagas. Behaviour matches the CALL handlers in redux/apps/sagas.js. */

type SagaWorker = (action: any) => any

const getApps = () => reduxStore.getState().apps

// This slice never writes `apps.active` or `apps.loading` — both belong to the apps slice.

interface GetCallLogsPayload {
  data?: ChatApp
  appSettings?: unknown
  currentPage?: number
  /** Epoch MILLISECONDS from the UI — converted to seconds before the request. */
  fromDate?: number
  toDate?: number
  filters?: CallLogsFilters
}

export function* GET_CALL_LOGS({ payload }: { payload: GetCallLogsPayload }) {
  yield put({ type: actions.SET_STATE, payload: { areCallLogsLoading: true } })

  const apps = getApps()
  const app = (payload.data ?? apps.active) as ChatApp
  const appSettings = payload.appSettings ?? apps.appSettings

  // Without appRegion.meta the host resolves to `call-undefined.…`, so bail early.
  if (!app?.appRegion?.meta) {
    yield put({
      type: actions.SET_STATE,
      payload: { areCallLogsLoading: false, callSearchLogs: [] },
    })
    return
  }

  try {
    const { fromDate, toDate } = payload
    // The API takes epoch SECONDS; 0 means "no bound".
    const startRange = fromDate ? Math.floor(fromDate / 1000) : 0
    const endRange = toDate ? Math.floor(toDate / 1000) : 0

    const success: CallLogsListResponse = yield call(
      callLogs,
      app,
      getAppCallsHost(appSettings),
      'calls',
      startRange,
      endRange,
      payload.filters || {},
      payload.currentPage || 1,
    )

    if (success && success.data) {
      const callLogData = success.data
      let callSearchLogsTotal = callLogData.length
      let callSearchLogsPagination: LoosePagination = {}

      if (success.meta && success.meta.pagination) {
        callSearchLogsTotal = success.meta.pagination.total || callLogData.length
        callSearchLogsPagination = success.meta.pagination
      }

      yield put({
        type: actions.SET_STATE,
        payload: {
          callSearchLogs: callLogData,
          callSearchLogsTotal,
          callSearchLogsPagination,
          areCallLogsLoading: false,
        },
      })
    } else {
      yield put({
        type: actions.SET_STATE,
        payload: {
          callSearchLogs: [],
          callSearchLogsTotal: 0,
          callSearchLogsPagination: {},
          areCallLogsLoading: false,
        },
      })
    }
  } catch (error) {
    if (window.triggerNotification) {
      window.triggerNotification('Failed to load call logs', 'error')
    }
    yield put({
      type: actions.SET_STATE,
      payload: {
        callSearchLogs: [],
        callSearchLogsTotal: 0,
        callSearchLogsPagination: {},
        areCallLogsLoading: false,
      },
    })
  }
}

interface GetCallByIdPayload {
  id?: string
  /** `mid` from the route — selects between records sharing a sessionId. */
  id2?: string
  data?: ChatApp
  appSettings?: unknown
}

export function* GET_CALL_BY_ID({ payload }: { payload: GetCallByIdPayload }) {
  if (!payload || payload.id === undefined) {
    yield put({
      type: actions.SET_STATE,
      payload: {
        callActive: null,
        callSessionParticipants: [],
        callRecordings: [],
        areCallDetailsLoading: false,
      },
    })
    return
  }

  // Clear the previous call so its details never render under the new call's URL.
  yield put({
    type: actions.SET_STATE,
    payload: {
      callActive: null,
      callSessionParticipants: [],
      callRecordings: [],
      areCallDetailsLoading: true,
    },
  })

  const apps = getApps()
  const app = (payload.data ?? apps.active) as ChatApp
  const appSettings = payload.appSettings ?? apps.appSettings

  if (!app?.appRegion?.meta) {
    yield put({
      type: actions.SET_STATE,
      payload: { callActive: null, areCallDetailsLoading: false },
    })
    return
  }

  try {
    const success: CallDetailResponse | null = yield call(
      getCallDetail,
      app,
      getAppCallsHost(appSettings),
      payload.id,
    )

    if (success && success.data && success.data.length > 0) {
      // One sessionId can hold many records (a captured meet returns 26), so
      // pick the one matching the route's `mid`, falling back to the first.
      let currentCall: CallLog = success.data[0]
      if (success.data.length > 1) {
        const matched = success.data.find(record => record.mid === payload.id2)
        currentCall = matched ?? success.data[0]
      }

      yield put({
        type: actions.SET_STATE,
        payload: {
          callActive: currentCall,
          callSessionParticipants: currentCall.participants ?? [],
          // `recordings` is absent whenever a call has none — `hasRecording:
          // true` does not imply it.
          callRecordings: currentCall.recordings ?? [],
          areCallDetailsLoading: false,
        },
      })
    } else {
      yield put({
        type: actions.SET_STATE,
        payload: {
          callActive: null,
          callSessionParticipants: [],
          callRecordings: [],
          areCallDetailsLoading: false,
        },
      })
    }
  } catch (error) {
    if (window.triggerNotification) {
      window.triggerNotification('Failed to load call details', 'error')
    }
    yield put({
      type: actions.SET_STATE,
      payload: {
        callActive: null,
        callSessionParticipants: [],
        callRecordings: [],
        areCallDetailsLoading: false,
      },
    })
  }
}

interface DownloadRecordingPayload {
  /** A record's `recording_url`. */
  fileUrl: string
  /** UI-generated label, e.g. "Recording 1" — the service appends `.mp4`. */
  fileName: string
}

export function* DOWNLOAD_CALL_RECORDING({ payload }: { payload: DownloadRecordingPayload }) {
  const response: ArrayBuffer | DownloadRecordingError = yield call(
    downloadCallRecording,
    payload.fileUrl,
    payload.fileName,
  )

  const failed = (response as DownloadRecordingError)?.error
  if (failed) {
    window.triggerNotification(failed, 'error')
  } else {
    // Not `info` — the shared notification never auto-dismisses that type.
    window.triggerNotification('Recording download in progress...', 'success')
  }
}

/* ── Legacy (v1 metrics API) ─────────────────────────────────────────────── */

interface GetLegacyCallLogsPayload {
  data?: LegacyCallsApp
  appSettings?: unknown
  /** Epoch MILLISECONDS. Unlike v3, v1 takes ms — do not divide by 1000. */
  fromDate?: number
  toDate?: number
}

function notifyLegacyFailure(result: unknown, fallback: string): void {
  const message = (result as LegacyApiErrorBody)?.error?.message
  window.triggerNotification?.(message || fallback, 'error')
}

/** v1 returns the whole window in one body, so the caller's range is the only bound. */
function defaultLegacyRange(): { startRange: number; endRange: number } {
  const nowDate = new Date()
  const todayStartTime = new Date(
    nowDate.getFullYear(),
    nowDate.getMonth(),
    nowDate.getDate(),
    0,
    0,
    0,
  )
  return {
    startRange: new Date(todayStartTime.getTime() - 7 * 24 * 60 * 60 * 1000).getTime(),
    endRange: nowDate.getTime(),
  }
}

// Callers must wait for apps.appSettings: without REGION the metrics host
// resolves to `metrics-undefined.…` and every request 404s.
export function* GET_LEGACY_CALL_LOGS({ payload }: { payload: GetLegacyCallLogsPayload }) {
  const { startRange, endRange } =
    payload?.fromDate !== undefined && payload?.toDate !== undefined
      ? { startRange: payload.fromDate, endRange: payload.toDate }
      : defaultLegacyRange()
  const fromDate = Math.floor(startRange)
  const toDate = Math.floor(endRange)

  // The range the saga settles on is stored, so the UI shows the real window — default included.
  yield put({
    type: actions.SET_STATE,
    payload: { areLegacyCallLogsLoading: true, legacyCallLogsRange: { fromDate, toDate } },
  })

  const apps = getApps()
  const app = (payload?.data ?? apps.active) as LegacyCallsApp
  const appSettings = payload?.appSettings ?? apps.appSettings

  try {
    const success: LegacyCallSessionsResult = yield call(
      legacyCallLogs,
      app,
      getAppMetricHost(appSettings),
      'calls',
      fromDate,
      toDate,
    )

    if (isLegacyFailure(success)) {
      notifyLegacyFailure(success, 'Failed to load legacy call logs')
      yield put({
        type: actions.SET_STATE,
        payload: { legacyCallSearchLogs: [], areLegacyCallLogsLoading: false },
      })
      return
    }

    const sessions = (success as LegacyCallSessionsResponse)?.data
    if (sessions) {
      yield put({
        type: actions.SET_STATE,
        payload: {
          legacyCallSearchLogs: _.reverse(_.sortBy(sessions, ['start_time'])),
          areLegacyCallLogsLoading: false,
        },
      })
    } else {
      yield put({
        type: actions.SET_STATE,
        payload: { legacyCallSearchLogs: [], areLegacyCallLogsLoading: false },
      })
    }
  } catch (error) {
    if (window.triggerNotification) {
      window.triggerNotification('Failed to load legacy call logs', 'error')
    }
    yield put({
      type: actions.SET_STATE,
      payload: { legacyCallSearchLogs: [], areLegacyCallLogsLoading: false },
    })
  }
}

interface GetLegacyCallByIdPayload {
  id?: string
  data?: LegacyCallsApp
  appSettings?: unknown
  /**
   * The record's own range. It is the only discriminator: one session id can
   * return hundreds of records, and omitting it yields an arbitrary one.
   */
  beginningTimestamp?: number
  endingTimestamp?: number
}

export function* GET_LEGACY_CALL_BY_ID({ payload }: { payload: GetLegacyCallByIdPayload }) {
  if (!payload || payload.id === undefined) {
    yield put({
      type: actions.SET_STATE,
      payload: { legacyCallActive: null, areLegacyCallDetailsLoading: false },
    })
    return
  }

  // Cleared up front so the previous call never renders under the new one's URL.
  yield put({
    type: actions.SET_STATE,
    payload: {
      legacyCallActive: null,
      legacyCallSessionParticipants: [],
      areLegacyCallDetailsLoading: true,
    },
  })

  const apps = getApps()
  const app = (payload.data ?? apps.active) as LegacyCallsApp
  const appSettings = payload.appSettings ?? apps.appSettings

  try {
    const success: LegacyCallSessionsResult = yield call(
      legacyCallDetail,
      app,
      getAppMetricHost(appSettings),
      payload.id,
      payload.beginningTimestamp,
      payload.endingTimestamp,
    )

    if (isLegacyFailure(success)) {
      notifyLegacyFailure(success, 'Failed to load legacy call details')
      yield put({
        type: actions.SET_STATE,
        payload: { legacyCallActive: null, areLegacyCallDetailsLoading: false },
      })
      return
    }

    const sessions = (success as LegacyCallSessionsResponse)?.data
    if (sessions && sessions.length) {
      yield put({
        type: actions.SET_STATE,
        payload: { legacyCallActive: sessions[0], areLegacyCallDetailsLoading: false },
      })
      yield put({ type: actions.GET_LEGACY_CALL_PARTICIPANTS, payload })
    } else {
      yield put({
        type: actions.SET_STATE,
        payload: { legacyCallActive: null, areLegacyCallDetailsLoading: false },
      })
    }
  } catch (error) {
    if (window.triggerNotification) {
      window.triggerNotification('Failed to load legacy call details', 'error')
    }
    yield put({
      type: actions.SET_STATE,
      payload: { legacyCallActive: null, areLegacyCallDetailsLoading: false },
    })
  }
}

/** Substring match on `device_id`, which is an empty string on many real rows. */
function toPlatform(deviceId: string): LegacyParticipantPlatform {
  if (_.includes(deviceId, 'WEB', 0)) return 'Web'
  if (_.includes(deviceId, 'Android', 0)) return 'Android'
  if (_.includes(deviceId, 'ios', 0)) return 'IOS'
  if (_.includes(deviceId, 'ionic', 0)) return 'Ionic'
  if (_.includes(deviceId, 'react_native', 0)) return 'React Native'
  return 'Other'
}

/** v1 reports `audio_minutes`; there is no `voice_minutes` field to read. */
function toMinutes(value?: number): number | '-' {
  return value && value > 0 ? value : '-'
}

function toParticipantRow(
  participant: LegacyCallParticipant,
  user?: ChatUser,
): LegacyCallParticipantRow {
  const base = {
    uid: participant.uid,
    platform: toPlatform(participant.device_id),
    audioMinutes: toMinutes(participant.audio_minutes),
    videoMinutes: toMinutes(participant.video_minutes),
  }

  if (!user) {
    return { ...base, name: 'User', role: '-', isDeleted: true }
  }

  return {
    ...base,
    name: user.name,
    role: user.role ?? '',
    isDeleted: false,
    avatar: user.avatar ?? '',
    start_time: participant.start_time,
  }
}

export function* GET_LEGACY_CALL_PARTICIPANTS({ payload }: { payload: GetLegacyCallByIdPayload }) {
  if (!payload || payload.id === undefined) {
    yield put({
      type: actions.SET_STATE,
      payload: { legacyCallSessionParticipants: [], areLegacyCallParticipantsLoading: false },
    })
    return
  }

  yield put({ type: actions.SET_STATE, payload: { areLegacyCallParticipantsLoading: true } })

  const apps = getApps()
  const app = (payload.data ?? apps.active) as LegacyCallsApp
  const appSettings = payload.appSettings ?? apps.appSettings

  try {
    const success: LegacyCallParticipantsResult = yield call(
      legacyCallParticipants,
      app,
      getAppMetricHost(appSettings),
      'calls',
      payload.id,
      payload.beginningTimestamp,
      payload.endingTimestamp,
    )

    if (isLegacyFailure(success)) {
      notifyLegacyFailure(success, 'Failed to load call participants')
      yield put({
        type: actions.SET_STATE,
        payload: { legacyCallSessionParticipants: [], areLegacyCallParticipantsLoading: false },
      })
      return
    }

    const participants = (success as LegacyCallParticipantsResponse)?.data
    if (!participants || !participants.length) {
      yield put({
        type: actions.SET_STATE,
        payload: { legacyCallSessionParticipants: [], areLegacyCallParticipantsLoading: false },
      })
      return
    }

    // v1 carries no name/role/avatar, so they are joined in from the users API.
    const uids = _.uniq(_.map(participants, 'uid')).join(',')
    const users: UsersListResponse = yield call(
      getMultipleUsers,
      uids,
      app,
      getAppHost(appSettings),
    )

    const rows = participants.map(participant =>
      toParticipantRow(
        participant,
        (users?.data ?? []).find(user => user.uid === participant.uid),
      ),
    )

    yield put({
      type: actions.SET_STATE,
      payload: { legacyCallSessionParticipants: rows, areLegacyCallParticipantsLoading: false },
    })
  } catch (error) {
    if (window.triggerNotification) {
      window.triggerNotification('Failed to load call participants', 'error')
    }
    yield put({
      type: actions.SET_STATE,
      payload: { legacyCallSessionParticipants: [], areLegacyCallParticipantsLoading: false },
    })
  }
}

interface DownloadLegacyRecordingPayload {
  /** A record's `file_url`. */
  fileUrl: string
  /** UI-composed label; the service appends `.mp4`. */
  fileName: string
}

export function* DOWNLOAD_LEGACY_CALL_RECORDING({
  payload,
}: {
  payload: DownloadLegacyRecordingPayload
}) {
  const response: ArrayBuffer | LegacyDownloadRecordingError = yield call(
    downloadLegacyRecording,
    payload.fileUrl,
    payload.fileName,
  )

  const failed = (response as LegacyDownloadRecordingError)?.error
  if (failed) {
    window.triggerNotification(failed, 'error')
  } else {
    // Not `info` — the shared notification never auto-dismisses that type.
    window.triggerNotification('Recording download in progress...', 'success')
  }
}

export default function* callsSaga() {
  yield all([
    // Latest-only: pagination and filters can resolve out of order otherwise.
    takeLatest(actions.GET_CALL_LOGS, GET_CALL_LOGS as SagaWorker),
    takeLatest(actions.GET_CALL_BY_ID, GET_CALL_BY_ID as SagaWorker),
    takeEvery(actions.DOWNLOAD_CALL_RECORDING, DOWNLOAD_CALL_RECORDING as SagaWorker),

    takeLatest(actions.GET_LEGACY_CALL_LOGS, GET_LEGACY_CALL_LOGS as SagaWorker),
    takeEvery(actions.GET_LEGACY_CALL_BY_ID, GET_LEGACY_CALL_BY_ID as SagaWorker),
    takeEvery(actions.GET_LEGACY_CALL_PARTICIPANTS, GET_LEGACY_CALL_PARTICIPANTS as SagaWorker),
    takeEvery(
      actions.DOWNLOAD_LEGACY_CALL_RECORDING,
      DOWNLOAD_LEGACY_CALL_RECORDING as SagaWorker,
    ),
  ])
}
